import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@material-ui/core';
import StringResourceManager, { GetString } from './StringResourceManager';
import { ApplicationState } from '../store';
import ListBox from './ListBox';
import {
  getLaneNumberByDivisionSiteNumberAndCluster,
  getNodeGroupByDivisionAndSiteNumber,
  getSiteNumberByDivision,
} from '../store/Assets/AssetsActions';
import enableIcon from '../assets/images/arrow-right.png';
import { NBCProfile } from '../Interfaces/NBC/NBCItem';

const useStyles = makeStyles(theme =>
  createStyles({
    gridAlign: {
      display: 'flex',
    },
    dialogLabel: {
      fontSize: '1.1rem',
      display: 'inline',
    },
    arrowIcon: {
      height: '50px',
      weight: '50px',
    },
    iconGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 10,
    },
    listBoxGrid: {
      width: '200px',
      marginRight: '10px',
    },
    listBox: {
      border: '1px solid #000000',
    },
    dropdownStyle: {
      marginTop: '25px',
      marginLeft: '25px',
    },
    buttonStyle: {
      alignItems: 'flex-end',
      margin: '25px',
    },
    enableButton: {
      width: '180px',
      border: '1px solid #000000',
    },
    disableButton: {
      width: '180px',
    },
  })
);

interface Props {
  selectedProfile?: string;
  setDivisionProfile: (stores: string[]) => void;
  setStoreProfile: (stores: string[]) => void;
  setNodeGroupProfile: (cluster: string[]) => void;
  setLanesProfile: (lanes: string[]) => void;
}

const ProfileNodeSelection: React.FC<Props> = ({
  setDivisionProfile,
  setStoreProfile,
  setNodeGroupProfile,
  setLanesProfile,
  selectedProfile,
}): React.ReactElement => {
  const classes = useStyles();

  const dispatch: Dispatch<any> = useDispatch();

  const divisions: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.divisions
  );

  const siteNumbersByDivision: string[] = useSelector(
    (state: ApplicationState) => state.assetsReducer.siteNumberByDivision
  );

  const clustersByDivisionAndSiteNumber: string[] = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.nodeGroupByDivisionAndSiteNumber
  );

  const laneNumberByDivisionStoreAndCluster: string[] = useSelector(
    (state: ApplicationState) =>
      state.assetsReducer.laneNumberByDivisionSiteNumberAndCluster
  );

  const profileNames: NBCProfile[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.profileNames
  );

  const selectedNBCProfile = profileNames.find(
    (a: NBCProfile) => a.ProfileName === selectedProfile
  );

  const [selectedDivision, setSelectedDivision] = React.useState<string>(
    selectedNBCProfile?.Divisions?.length === 1
      ? selectedNBCProfile?.Divisions[0]
      : ''
  );

  const [selectedDivisions, setSelectedDivisions] = React.useState<string[]>(
    []
  );

  const [selectedStores, setSelectedStores] = React.useState<string[]>([]);

  const [selectedLanes, setSelectedLanes] = React.useState<string[]>([]);

  const [selectedClusters, setSelectedClusters] = React.useState<string[]>([]);

  const [selectedStore, setSelectedStore] = React.useState<string>(selectedNBCProfile?.SiteNumbers?.length === 1
      ? selectedNBCProfile?.SiteNumbers[0]
      : '');

  const [selectedCluster, setSelectedCluster] = React.useState<string>('');

  const divisionCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems = selectedDivisions.includes(value)
      ? selectedDivisions.filter(item => item !== value)
      : [...selectedDivisions, value];

    setSelectedDivisions(updatedSelectedItems);
    setDivisionProfile(updatedSelectedItems);
    if (updatedSelectedItems.length === 0) {
      setSelectedStores([]);
      setSelectedClusters([]);
      setSelectedLanes([]);
    } else if (updatedSelectedItems.length === 1) {
      setSelectedDivision(updatedSelectedItems[0]);
      getSiteNumberByDivision(dispatch, updatedSelectedItems[0]);
      setSelectedStores([]);
      setSelectedClusters([]);
      setSelectedLanes([]);
    } else {
      setSelectedStores([]);
      setSelectedClusters([]);
      setSelectedLanes([]);
    }
  };

  const storeCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems: any = selectedStores.includes(value)
      ? selectedStores.filter(item => item !== value)
      : [...selectedStores, value];

    setSelectedStores(updatedSelectedItems);
    setStoreProfile(updatedSelectedItems);
    if (updatedSelectedItems.length === 1) {
      setSelectedStore(updatedSelectedItems[0]);
      getNodeGroupByDivisionAndSiteNumber(
        dispatch,
        selectedDivision,
        updatedSelectedItems[0]
      );
    }
    else {
      setSelectedClusters([]);
      setSelectedLanes([]);
    }
  };

  const clusterCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems = selectedClusters.includes(value)
      ? selectedClusters.filter(item => item !== value)
      : [...selectedClusters, value];

    setSelectedClusters(updatedSelectedItems);
    setNodeGroupProfile(updatedSelectedItems);

    if (updatedSelectedItems.length === 0) {
      setSelectedLanes([]);
    } else if (updatedSelectedItems.length === 1) {
      setSelectedCluster(updatedSelectedItems[0]);
      getLaneNumberByDivisionSiteNumberAndCluster(
        dispatch,
        selectedDivision,
        selectedStore,
        updatedSelectedItems[0]
      );
    }
  };

  const laneCheckBoxOnChange = (value: any) => {
    const updatedSelectedItems = selectedLanes.includes(value)
      ? selectedLanes.filter(item => item !== value)
      : [...selectedLanes, value];

    setSelectedLanes(updatedSelectedItems);
    setLanesProfile(updatedSelectedItems);
  };

  const handleToggleSelectAllDivision = () => {
    if (selectedDivisions.length === divisions?.length) {
      setDivisionProfile([]);
      setSelectedDivisions([]);
      setSelectedStores([]);
      setSelectedClusters([]);
      setSelectedLanes([]);
    } else {
      setDivisionProfile(divisions);
      setSelectedDivisions(divisions || []);
      setSelectedStores([]);
      setSelectedClusters([]);
      setSelectedLanes([]);
    }
  };

  const handleToggleSelectAllStore = () => {
    if (selectedStores.length === siteNumbersByDivision?.length) {
      setSelectedStores([]);
      setStoreProfile([]);
    } else {
      setStoreProfile(siteNumbersByDivision);
      setSelectedStores(siteNumbersByDivision || []);
      setSelectedClusters([]);
      setSelectedLanes([]);
    }
  };

  const handleToggleSelectAllClusters = () => {
    if (selectedClusters.length === clustersByDivisionAndSiteNumber?.length) {
      setSelectedClusters([]);
      setNodeGroupProfile([]);
    } else {
      setSelectedClusters(clustersByDivisionAndSiteNumber || []);
      setNodeGroupProfile(clustersByDivisionAndSiteNumber);
    }
  };

  const handleToggleSelectAllLanes = () => {
    if (selectedLanes.length === laneNumberByDivisionStoreAndCluster?.length) {
      setSelectedLanes([]);
      setLanesProfile([]);
    } else {
      setSelectedLanes(laneNumberByDivisionStoreAndCluster || []);
      setLanesProfile(laneNumberByDivisionStoreAndCluster);
    }
  };

  React.useEffect(() => {
    const EditSelectedProfile = profileNames.find(
      (a: NBCProfile) => a.ProfileName === selectedProfile
    );

    if (selectedNBCProfile?.Divisions?.length === 1) {
      const profileDivisions = EditSelectedProfile?.Divisions;

      if (profileDivisions && profileDivisions.length === 1) {

        if(!divisions?.includes(profileDivisions[0])) return
        
        getSiteNumberByDivision(dispatch, profileDivisions[0]);

        const siteNumbers = EditSelectedProfile?.SiteNumbers;

        if (siteNumbers && siteNumbers.length === 1) {
          getNodeGroupByDivisionAndSiteNumber(
            dispatch,
            profileDivisions[0],
            siteNumbers[0]
          );

          const nodeGroups = EditSelectedProfile?.NodeGroups;

          if (nodeGroups && nodeGroups.length === 1) {
            getLaneNumberByDivisionSiteNumberAndCluster(
              dispatch,
              profileDivisions[0],
              siteNumbers[0],
              nodeGroups[0]
            );
          }
        }
      }
    }

    setSelectedDivisions(EditSelectedProfile?.Divisions ?? []);
    setSelectedStores(EditSelectedProfile?.SiteNumbers ?? []);
    setSelectedClusters(EditSelectedProfile?.NodeGroups ?? []);
    setSelectedLanes(EditSelectedProfile?.LaneNumbers ?? []);
  }, [selectedProfile]);

  return (
    <React.Fragment>
      <Grid className={classes.listBoxGrid}>
        <Grid>
          <StringResourceManager
            variant="body1"
            style="dialogLabel"
            stringKey={'SELECT_DIVISION'}
            gutterBottom={true}
          />
        </Grid>
        <Grid className={classes.listBox}>
          <ListBox
            data={divisions}
            handleCheckBoxEnable={divisionCheckBoxOnChange}
            selectedItems={selectedDivisions}
            handleToggleSelectAll={handleToggleSelectAllDivision}
            listBoxHeight={300}
            ListItemFontSize={14}
          />
        </Grid>
      </Grid>
      <Grid
        className={classes.iconGrid}
        hidden={selectedDivisions.length === 1 ? false : true}
      >
        <img src={enableIcon} className={classes.arrowIcon} />
      </Grid>
      <Grid
        className={classes.listBoxGrid}
        hidden={selectedDivisions.length === 1 ? false : true}
      >
        <Grid>
          <StringResourceManager
            variant="body1"
            style="dialogLabel"
            stringKey={'SELECT_STORES'}
            gutterBottom={true}
          />
        </Grid>
        <Grid className={classes.listBox}>
          <ListBox
            data={siteNumbersByDivision}
            handleCheckBoxEnable={storeCheckBoxOnChange}
            selectedItems={selectedStores}
            handleToggleSelectAll={handleToggleSelectAllStore}
            listBoxHeight={300}
            ListItemFontSize={14}
          />
        </Grid>
      </Grid>
      <Grid
        className={classes.iconGrid}
        hidden={selectedStores.length === 1 ? false : true}
      >
        <img src={enableIcon} className={classes.arrowIcon} />
      </Grid>
      <Grid
        className={classes.listBoxGrid}
        hidden={selectedStores.length === 1 ? false : true}
      >
        <Grid>
          <StringResourceManager
            variant="body1"
            style="dialogLabel"
            stringKey={'SELECT_CLUSTERS'}
            gutterBottom={true}
          />
        </Grid>
        <Grid className={classes.listBox}>
          <ListBox
            data={clustersByDivisionAndSiteNumber}
            handleCheckBoxEnable={clusterCheckBoxOnChange}
            handleToggleSelectAll={handleToggleSelectAllClusters}
            selectedItems={selectedClusters}
            listBoxHeight={300}
            ListItemFontSize={14}
          />
        </Grid>
      </Grid>
      <Grid
        className={classes.iconGrid}
        hidden={selectedClusters.length === 1 ? false : true}
      >
        <img src={enableIcon} className={classes.arrowIcon} />
      </Grid>
      <Grid
        className={classes.listBoxGrid}
        hidden={selectedClusters.length === 1 ? false : true}
      >
        <Grid>
          <StringResourceManager
            variant="body1"
            style="dialogLabel"
            stringKey={'SELECT_LANES'}
            gutterBottom={true}
          />
        </Grid>
        <Grid className={classes.listBox}>
          <ListBox
            data={laneNumberByDivisionStoreAndCluster}
            handleCheckBoxEnable={laneCheckBoxOnChange}
            handleToggleSelectAll={handleToggleSelectAllLanes}
            selectedItems={selectedLanes}
            listBoxHeight={300}
            ListItemFontSize={14}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default ProfileNodeSelection;
